import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useClient } from "../context/ClientContext";
import { PageHeader, Section, Card, CardHeader, Pill, LoadingBar } from "../components/Primitives";
import { Sparkle, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const TYPE_TONE = { happy: "neutral", edge: "warn", compliance: "info", exception: "danger" };
const PRIO_TONE = { P0: "danger", P1: "warn", P2: "neutral" };

export default function ScenarioEngine() {
  const { activeClient, activeClientId } = useClient();
  const [workflows, setWorkflows] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [workflowId, setWorkflowId] = useState("");
  const [delta, setDelta] = useState("Pre-trade compliance rule v2.4 deploy: tightens issuer concentration cap from 5% to 3.5%.");
  const [focus, setFocus] = useState("compliance + edge cases at handoff boundaries");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function refresh() {
    const r = await api.get("/scenarios", { params: { client_id: activeClientId } });
    setScenarios(r.data);
  }

  useEffect(() => {
    if (!activeClientId) return;
    setScenarios([]); setWorkflowId("");
    api.get("/workflows", { params: { client_id: activeClientId } }).then((r) => {
      setWorkflows(r.data);
      if (r.data.length) setWorkflowId(r.data[0].id);
    });
    refresh();
  }, [activeClientId]); // eslint-disable-line

  async function handleGenerate(e) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/scenarios/generate", { workflow_id: workflowId, change_delta: delta, focus });
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  }

  return (
    <div data-testid="scenario-engine-page">
      <PageHeader
        overline={`Layer 3 · ${activeClient?.name || ""}`}
        title="Scenario Engine"
        description="Generate workflow-aware test scenarios from change deltas using Claude Sonnet 4.5."
        right={<Pill tone="ai" testid="ai-pill"><Sparkle size={11} className="mr-1" /> Claude Sonnet 4.5</Pill>}
      />
      <Section>
        <div className="grid grid-cols-12 gap-6">
          <Card ai className="col-span-12 lg:col-span-5 p-6" testid="generate-form-card">
            <div className="overline text-zinc-500 mb-4">Input</div>
            <form onSubmit={handleGenerate} className="space-y-4" data-testid="scenario-form">
              <div>
                <label className="overline text-zinc-500 mb-2 block">Workflow</label>
                <select
                  data-testid="scenario-workflow-select"
                  value={workflowId}
                  onChange={(e) => setWorkflowId(e.target.value)}
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2.5 text-sm bg-white focus:border-vlink-500 outline-none"
                >
                  {workflows.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              <div>
                <label className="overline text-zinc-500 mb-2 block">Change delta</label>
                <textarea
                  data-testid="scenario-delta-input"
                  value={delta}
                  onChange={(e) => setDelta(e.target.value)}
                  rows={4}
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2.5 text-sm font-mono bg-white focus:border-vlink-500 outline-none"
                />
              </div>
              <div>
                <label className="overline text-zinc-500 mb-2 block">Focus</label>
                <input
                  data-testid="scenario-focus-input"
                  value={focus}
                  onChange={(e) => setFocus(e.target.value)}
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2.5 text-sm bg-white focus:border-vlink-500 outline-none"
                />
              </div>
              {error && <div data-testid="scenario-error" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-sm">{error}</div>}
              <button
                data-testid="scenario-generate-btn"
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-vlink-500 hover:bg-vlink-600 text-white text-sm font-medium px-4 py-3 rounded-sm transition-colors disabled:opacity-60"
              >
                {busy ? <CircleNotch size={14} className="animate-spin" /> : <Sparkle size={14} />}
                {busy ? "Generating with Claude…" : "Generate Scenarios"}
              </button>
            </form>
          </Card>

          <div className="col-span-12 lg:col-span-7 space-y-4">
            <div className="flex items-center justify-between">
              <div className="overline text-zinc-500">Scenario library ({scenarios.length})</div>
              <Pill tone="neutral">Sorted by recent</Pill>
            </div>
            {scenarios.length === 0 ? (
              <LoadingBar label="No scenarios yet — generate one" />
            ) : (
              scenarios.map((s, idx) => (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: idx * 0.02 }}
                >
                  <Card className="p-5" testid={`scenario-${s.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className="font-heading text-base text-zinc-900">{s.title}</div>
                        <div className="text-xs text-zinc-500 font-mono mt-0.5">{s.workflow_name}</div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Pill tone={TYPE_TONE[s.type] || "neutral"}>{s.type}</Pill>
                        <Pill tone={PRIO_TONE[s.priority] || "neutral"}>{s.priority}</Pill>
                        {s.handoff_risk === "high" && <Pill tone="danger">handoff: high</Pill>}
                        {s.ai_generated && <Pill tone="ai"><Sparkle size={10} /></Pill>}
                      </div>
                    </div>
                    <ol className="mt-3 space-y-1 text-sm text-zinc-700">
                      {(s.steps || []).map((st, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="font-mono text-xs text-zinc-400 mt-0.5 min-w-[18px]">{String(i + 1).padStart(2, "0")}</span>
                          <span>{st}</span>
                        </li>
                      ))}
                    </ol>
                    {s.expected && (
                      <div className="mt-3 pt-3 border-t border-zinc-100 text-xs text-zinc-600 flex items-start gap-2">
                        <ArrowRight size={12} className="mt-0.5 flex-shrink-0 text-vlink-500" />
                        <span><span className="text-zinc-900 font-medium">Expected: </span>{s.expected}</span>
                      </div>
                    )}
                  </Card>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}
